import {envIsDev} from '../../../../config';
import {recordDatadogLogInfo} from '../../datadogLogs';
import {recordRumDurationVital} from '../../datadogRum';
import {CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME} from './constants';
import {LifecyclePainObserver} from './lifecyclePainObserver';
import {
  LoafObservationRuntime,
  type ReportProfilerUnavailable,
} from './loafObservationRuntime';
import type {
  CriticalWorkflowExplicitObservationHandle,
  CriticalWorkflowExplicitObservationOptions,
  CriticalWorkflowObservationOptions,
  RecordCriticalWorkflowDurationVital,
} from './types';
import {WindowedPainObserver} from './windowedPainObserver';
import {createWorkflowSamplingPolicy} from './workflowSamplingPolicy';

export {
  CRITICAL_WORKFLOW_EXPLICIT_PAIN_WINDOW_INSTRUMENTATION_VERSION,
  CRITICAL_WORKFLOW_PAIN_WINDOW_INSTRUMENTATION_VERSION,
  CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME,
  DEFAULT_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS,
} from './constants';

const shouldLogToConsole = envIsDev && false;

const defaultRecordDurationVital: RecordCriticalWorkflowDurationVital = (
  name,
  options
) => {
  if (shouldLogToConsole) {
    console.info(name, options);
  }
  recordRumDurationVital(name, options);
};

const defaultReportProfilerUnavailable: ReportProfilerUnavailable =
  missingCapabilities => {
    recordDatadogLogInfo('critical_workflow_pain_window_profiler_unavailable', {
      missingCapabilities,
    });
  };

export class CriticalWorkflowPainWindowMonitor {
  private readonly runtime: LoafObservationRuntime;
  private readonly windowedObserver: WindowedPainObserver;
  private readonly lifecycleObserver: LifecyclePainObserver;
  private initializationCount = 0;

  constructor(
    recordDurationVital: RecordCriticalWorkflowDurationVital = defaultRecordDurationVital,
    reportProfilerUnavailable: ReportProfilerUnavailable = defaultReportProfilerUnavailable
  ) {
    // A timed window and a caller-stopped session for the same workflow share
    // a cooldown. Switching between them must not allow an extra start.
    const samplingPolicy = createWorkflowSamplingPolicy();
    this.runtime = new LoafObservationRuntime(reportProfilerUnavailable);
    this.windowedObserver = new WindowedPainObserver(
      this.runtime,
      recordDurationVital,
      samplingPolicy
    );
    this.lifecycleObserver = new LifecyclePainObserver(
      this.runtime,
      recordDurationVital,
      samplingPolicy
    );
  }

  initialize(): () => void {
    const stopEventTiming = this.windowedObserver.initialize();
    if (!this.runtime.isSupported()) {
      return stopEventTiming;
    }

    this.initializationCount += 1;
    if (this.initializationCount === 1) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      window.addEventListener('pagehide', this.handlePageHide);
      document.addEventListener('freeze', this.handlePageHide);
    }

    let stopped = false;
    return () => {
      if (stopped) {
        return;
      }
      stopped = true;
      stopEventTiming();
      this.initializationCount -= 1;
      if (this.initializationCount === 0) {
        document.removeEventListener(
          'visibilitychange',
          this.handleVisibilityChange
        );
        window.removeEventListener('pagehide', this.handlePageHide);
        document.removeEventListener('freeze', this.handlePageHide);
      }
    };
  }

  observe(options: CriticalWorkflowObservationOptions): boolean {
    return this.windowedObserver.observe(options);
  }

  beginExplicit(
    options: CriticalWorkflowExplicitObservationOptions
  ): CriticalWorkflowExplicitObservationHandle | undefined {
    return this.lifecycleObserver.begin(options);
  }

  private readonly handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.windowedObserver.markBackgrounded();
      this.lifecycleObserver.markBackgrounded();
    }
  };

  private readonly handlePageHide = () => {
    this.handlePageAbandonment();
  };

  private handlePageAbandonment(): void {
    this.lifecycleObserver.handlePageAbandonment();
    this.windowedObserver.handlePageAbandonment();
  }
}

const criticalWorkflowPainWindowMonitor =
  new CriticalWorkflowPainWindowMonitor();

export const initializeCriticalWorkflowPainWindowMonitoring =
  (): (() => void) => criticalWorkflowPainWindowMonitor.initialize();

export const observeCriticalWorkflowPainWindow = (
  options: CriticalWorkflowObservationOptions
): boolean => criticalWorkflowPainWindowMonitor.observe(options);

export const beginCriticalWorkflowPainWindow = (
  options: CriticalWorkflowExplicitObservationOptions
): CriticalWorkflowExplicitObservationHandle | undefined =>
  criticalWorkflowPainWindowMonitor.beginExplicit(options);
